// One row per FIELD of the target model, with the file's columns in the dropdown.
//
// Read left to right this says "here is what the system needs, tell me where it is in
// your file" - which is the direction the model, not the file, defines. The previous
// layout listed the file's columns on the left, so a field the file had no column for
// was invisible: nothing on screen said Currency was still needed. Now every field is a
// row, required ones are labelled, and the ones with no column carry a note that they
// get filled in on the review step.
//
// Two fields may point at the same column. For the month/year halves of a date group
// that is the intended way to handle a file with a single date column; anywhere else it
// is flagged.
import {
    Alert,
    Box,
    Chip,
    FormControl,
    ListSubheader,
    MenuItem,
    Paper,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
    alpha,
} from '@mui/material';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useMemo } from 'react';
import {
    FieldMappings,
    MappingSource,
    getFieldsUsingColumn,
    getMissingRequiredFields,
    getUnusedColumns,
} from './ColumnMapping';
import type { ImportFieldDescriptor } from './ImportModelRegistry';

const NOT_IN_FILE_VALUE = '__not_in_file__';
const MAX_SAMPLE_VALUES = 3;

const SOURCE_LABELS: Record<MappingSource, string> = {
    user: 'You set this',
    saved: 'Remembered from a previous upload',
    ai: 'Suggested',
    auto: 'Matched automatically',
};

function confidenceLabel(entry: FieldMappings[string]): string {
    if (entry.source === 'user') return 'Set';
    if (entry.source === 'saved') return 'Saved';
    if (entry.confidence === undefined) return 'Matched';
    if (entry.confidence >= 0.95) return 'High';
    if (entry.confidence >= 0.85) return 'Medium';
    return 'Low';
}

function confidenceColor(entry: FieldMappings[string]): 'success' | 'info' | 'warning' | 'default' {
    if (entry.source === 'user' || entry.source === 'saved') return 'info';
    if (entry.confidence === undefined) return 'default';
    if (entry.confidence >= 0.95) return 'success';
    if (entry.confidence >= 0.85) return 'info';
    return 'warning';
}

function formatSample(value: unknown): string {
    if (value === null || value === undefined || value === '') return '(blank)';
    const text = String(value);
    return text.length > 24 ? `${text.slice(0, 24)}...` : text;
}

export interface ColumnMappingTableProps {
    /** Column names found in the uploaded file. */
    columns: string[];
    /** First few values per column, used to help the user recognise the data. */
    sampleValues: Record<string, unknown[]>;
    /** Mappable target fields for the chosen model (derived fields excluded). */
    fields: ImportFieldDescriptor[];
    mappings: FieldMappings;
    /** `column` is null when the field is not in the file. */
    onChangeMapping: (fieldKey: string, column: string | null) => void;
}

export default function ColumnMappingTable(props: ColumnMappingTableProps) {
    const { columns, sampleValues, fields, mappings, onChangeMapping } = props;

    const fieldsByKey = useMemo(() => new Map(fields.map(f => [f.key, f])), [fields]);

    const missingRequired = useMemo(
        () => getMissingRequiredFields(mappings, fields),
        [mappings, fields]
    );
    const unusedColumns = useMemo(
        () => getUnusedColumns(mappings, columns),
        [mappings, columns]
    );

    const mappedFieldCount = useMemo(
        () => fields.filter(f => mappings[f.key]?.column).length,
        [fields, mappings]
    );

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography variant="subtitle1">Column mapping</Typography>
                <Chip
                    size="small"
                    label={`${mappedFieldCount} of ${fields.length} fields mapped`}
                    color={mappedFieldCount === fields.length ? 'success' : 'default'}
                />
                {missingRequired.length > 0 && (
                    <Chip
                        size="small"
                        icon={<WarningAmberIcon />}
                        color="warning"
                        variant="outlined"
                        label={`${missingRequired.length} required field${
                            missingRequired.length === 1 ? '' : 's'
                        } not in the file`}
                    />
                )}
            </Box>

            {missingRequired.length > 0 && (
                <Alert severity="warning" variant="outlined">
                    No column maps to{' '}
                    <strong>{missingRequired.map(f => f.label).join(', ')}</strong>. If your file
                    does not carry {missingRequired.length === 1 ? 'it' : 'them'}, leave{' '}
                    {missingRequired.length === 1 ? 'it' : 'them'} as{' '}
                    <em>Not in file</em> and fill{' '}
                    {missingRequired.length === 1 ? 'the value' : 'the values'} in on the next
                    step - the review grid can set one value across every row at once.
                </Alert>
            )}

            {unusedColumns.length > 0 && (
                <Alert severity="info" variant="outlined">
                    {unusedColumns.length} column{unusedColumns.length === 1 ? '' : 's'} in the
                    file {unusedColumns.length === 1 ? 'is' : 'are'} not being imported:{' '}
                    <strong>{unusedColumns.join(', ')}</strong>. That is usually fine for internal
                    reference columns.
                </Alert>
            )}

            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 420 }}>
                <Table size="small" stickyHeader>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 600, width: 240 }}>Field</TableCell>
                            <TableCell sx={{ fontWeight: 600, width: 280 }}>Column in file</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Sample values</TableCell>
                            <TableCell sx={{ fontWeight: 600, width: 110 }}>Match</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {fields.map(field => {
                            const entry = mappings[field.key]
                                ?? { column: null, source: 'auto' as MappingSource };
                            const isMapped = entry.column !== null;
                            const isMissingRequired = field.required && !isMapped;

                            // Other fields reading the same column. Expected between the
                            // month and year halves of a date group, worth a warning
                            // anywhere else.
                            const alsoUsing = isMapped
                                ? getFieldsUsingColumn(mappings, entry.column!)
                                    .filter(key => key !== field.key)
                                    .map(key => fieldsByKey.get(key))
                                    .filter((f): f is ImportFieldDescriptor => !!f)
                                : [];
                            const sharedWithDateGroup = alsoUsing.filter(
                                f => f.dateGroup && f.dateGroup === field.dateGroup
                            );
                            const sharedUnexpectedly = alsoUsing.filter(
                                f => !f.dateGroup || f.dateGroup !== field.dateGroup
                            );

                            const samples = isMapped
                                ? (sampleValues[entry.column!] ?? []).slice(0, MAX_SAMPLE_VALUES)
                                : [];

                            return (
                                <TableRow
                                    key={field.key}
                                    sx={{
                                        // Warning, not error: a required field with no column is
                                        // legitimate - the value gets filled in on the next step.
                                        // Note the palette has no `warning.50` - that key
                                        // resolves to undefined and renders no highlight at all.
                                        backgroundColor: isMissingRequired
                                            ? theme => alpha(theme.palette.warning.main, 0.09)
                                            : undefined,
                                        borderLeft: isMissingRequired ? 3 : 0,
                                        borderLeftColor: 'warning.main',
                                    }}
                                >
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                {field.label}
                                            </Typography>
                                            {field.required && (
                                                <Chip
                                                    size="small"
                                                    label="Required"
                                                    color={isMapped ? 'default' : 'warning'}
                                                    variant="outlined"
                                                    sx={{ height: 18, fontSize: '0.65rem' }}
                                                />
                                            )}
                                        </Box>
                                        {field.hint && (
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                sx={{ display: 'block' }}
                                            >
                                                {field.hint}
                                            </Typography>
                                        )}
                                        {isMissingRequired && (
                                            <Typography
                                                variant="caption"
                                                color="warning.dark"
                                                sx={{ display: 'block' }}
                                            >
                                                Fill in on the next step
                                            </Typography>
                                        )}
                                    </TableCell>

                                    <TableCell>
                                        <FormControl size="small" fullWidth>
                                            <Select
                                                value={entry.column ?? NOT_IN_FILE_VALUE}
                                                onChange={event => {
                                                    const next = event.target.value;
                                                    onChangeMapping(
                                                        field.key,
                                                        next === NOT_IN_FILE_VALUE ? null : next
                                                    );
                                                }}
                                                renderValue={value => {
                                                    if (value === NOT_IN_FILE_VALUE) {
                                                        return (
                                                            <Typography
                                                                variant="body2"
                                                                sx={{
                                                                    color: field.required
                                                                        ? 'warning.dark'
                                                                        : 'text.secondary',
                                                                    fontStyle: 'italic',
                                                                }}
                                                            >
                                                                Not in file
                                                            </Typography>
                                                        );
                                                    }
                                                    return String(value);
                                                }}
                                            >
                                                <MenuItem value={NOT_IN_FILE_VALUE}>
                                                    <em>Not in file</em>
                                                </MenuItem>
                                                <ListSubheader>Columns in your file</ListSubheader>
                                                {columns.map(column => (
                                                    <MenuItem key={column} value={column}>
                                                        {column}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>

                                        {sharedWithDateGroup.length > 0 && (
                                            <Tooltip
                                                title={`The month and the year are both read out of this column's dates.`}
                                            >
                                                <Chip
                                                    size="small"
                                                    variant="outlined"
                                                    color="info"
                                                    icon={<CalendarMonthIcon />}
                                                    sx={{ mt: 0.5, height: 20, fontSize: '0.65rem' }}
                                                    label={`Shared with ${sharedWithDateGroup
                                                        .map(f => f.label)
                                                        .join(', ')}`}
                                                />
                                            </Tooltip>
                                        )}
                                        {sharedUnexpectedly.length > 0 && (
                                            <Typography
                                                variant="caption"
                                                color="warning.dark"
                                                sx={{ display: 'block', mt: 0.5 }}
                                            >
                                                Also mapped to{' '}
                                                {sharedUnexpectedly.map(f => f.label).join(', ')}
                                            </Typography>
                                        )}
                                    </TableCell>

                                    <TableCell>
                                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                            {samples.length === 0 ? (
                                                <Typography variant="caption" color="text.secondary">
                                                    {isMapped ? '(no data)' : '-'}
                                                </Typography>
                                            ) : (
                                                samples.map((value, index) => (
                                                    <Chip
                                                        key={`${field.key}-sample-${index}`}
                                                        size="small"
                                                        variant="outlined"
                                                        label={formatSample(value)}
                                                    />
                                                ))
                                            )}
                                        </Box>
                                    </TableCell>

                                    <TableCell>
                                        {isMapped ? (
                                            <Tooltip title={SOURCE_LABELS[entry.source]}>
                                                <Chip
                                                    size="small"
                                                    label={confidenceLabel(entry)}
                                                    color={confidenceColor(entry)}
                                                    variant="outlined"
                                                />
                                            </Tooltip>
                                        ) : (
                                            <Typography variant="caption" color="text.secondary">
                                                -
                                            </Typography>
                                        )}
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
}
